import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ApiClient from '../../utils/ApiClient';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

function ImpactReport() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [topCompetitors, setTopCompetitors] = useState([]);
    const [sortColumn, setSortColumn] = useState('quantity');
    const [sortDirection, setSortDirection] = useState('desc');

    useEffect(() => {
        fetchReport();
        fetchTopCompetitors();
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await ApiClient.post('/api/impact-report', {});
            setResults(response.data || []);
        } catch (err) {
            setError('Failed to load report data');
            console.error('Error fetching report:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchTopCompetitors = async () => {
        try {
            const response = await ApiClient.post('/api/impact-report/top-competitors', {});
            setTopCompetitors(response.data || []);
        } catch (err) {
            console.error('Error fetching top competitors:', err);
        }
    };

    const chartData = {
        labels: topCompetitors.map(c => c.competitor),
        datasets: [
            {
                label: 'Buybox Wins',
                data: topCompetitors.map(c => c.buyboxCount),
                backgroundColor: [
                    'rgba(239, 68, 68, 0.8)',
                    'rgba(249, 115, 22, 0.8)',
                    'rgba(245, 158, 11, 0.8)',
                    'rgba(234, 179, 8, 0.8)',
                    'rgba(132, 204, 22, 0.8)',
                    'rgba(34, 197, 94, 0.8)',
                    'rgba(20, 184, 166, 0.8)',
                    'rgba(6, 182, 212, 0.8)',
                    'rgba(59, 130, 246, 0.8)',
                    'rgba(139, 92, 246, 0.8)',
                ],
                borderColor: [
                    'rgb(239, 68, 68)',
                    'rgb(249, 115, 22)',
                    'rgb(245, 158, 11)',
                    'rgb(234, 179, 8)',
                    'rgb(132, 204, 22)',
                    'rgb(34, 197, 94)',
                    'rgb(20, 184, 166)',
                    'rgb(6, 182, 212)',
                    'rgb(59, 130, 246)',
                    'rgb(139, 92, 246)',
                ],
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: 'Top 10 Competitors by Buybox Wins (On Our Products)',
                font: {
                    size: 16,
                },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Number of Buybox Wins',
                },
            },
        },
    };

    const formatSalesRank = (rank) => {
        if (rank === null || rank === undefined) return '-';
        return rank.toLocaleString();
    };

    const formatCategory = (category) => {
        if (!category) return '-';
        return category.replace(/_/g, ' ').replace(/display on website/gi, '').trim();
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return '$' + value.toFixed(2);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    const sortedResults = [...results].sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        // Handle null/undefined values - push to end
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        // String comparison for text fields
        if (sortColumn === 'asin' || sortColumn === 'productName' || sortColumn === 'sku' ||
            sortColumn === 'productCategory' || sortColumn === 'buyboxWinner') {
            aVal = (aVal || '').toLowerCase();
            bVal = (bVal || '').toLowerCase();
            if (sortDirection === 'asc') {
                return aVal.localeCompare(bVal);
            }
            return bVal.localeCompare(aVal);
        }

        // Numeric comparison
        if (sortDirection === 'asc') {
            return aVal - bVal;
        }
        return bVal - aVal;
    });

    const SortIcon = ({ column }) => {
        if (sortColumn !== column) {
            return <span className="ml-1 text-gray-300">&#8597;</span>;
        }
        return sortDirection === 'asc'
            ? <span className="ml-1">&#8593;</span>
            : <span className="ml-1">&#8595;</span>;
    };

    const exportToCsv = () => {
        if (sortedResults.length === 0) return;

        const headers = ['ASIN', 'Amazon Link', 'Product Name', 'SKU', 'Quantity', 'Sales Rank', 'Category', 'MAP Price', 'Landed Price', 'Buybox Winner', 'Reason'];
        const rows = sortedResults.map(r => [
            r.asin,
            `https://amazon.com/dp/${r.asin}`,
            `"${(r.productName || '').replace(/"/g, '""')}"`,
            r.sku || '',
            r.quantity,
            r.salesRank || '',
            `"${(r.productCategory || '').replace(/"/g, '""')}"`,
            r.mapPrice || '',
            r.landedPrice || '',
            r.buyboxWinner || '',
            (r.landedPrice && r.mapPrice && r.landedPrice < r.mapPrice) ? 'MAPP Violation' : ''
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `impact_report_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    // Calculate summary stats
    const totalUnits = results.reduce((sum, r) => sum + (r.quantity || 0), 0);
    const buyboxWins = results.filter(r => r.buyboxWinner === "Occam's Razor").length;
    const buyboxLosses = results.filter(r => r.buyboxWinner && r.buyboxWinner !== "Occam's Razor" && r.buyboxWinner !== 'Unknown').length;

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">{error}</div>
                <button
                    onClick={fetchReport}
                    className="text-blue-600 hover:text-blue-800"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Impact Report
                </h2>
                <p className="text-gray-600 mb-6">
                    Shows all current listings with at least 1 unit in stock, along with buybox status, sales rank, and category.
                </p>

                {/* Summary Stats */}
                {!loading && results.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-500">Total Listings</div>
                            <div className="text-2xl font-semibold text-gray-900">{results.length}</div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-500">Total Units</div>
                            <div className="text-2xl font-semibold text-gray-900">{totalUnits.toLocaleString()}</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="text-sm text-green-600">Buybox Wins</div>
                            <div className="text-2xl font-semibold text-green-700">{buyboxWins}</div>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg">
                            <div className="text-sm text-red-600">Buybox Losses</div>
                            <div className="text-2xl font-semibold text-red-700">{buyboxLosses}</div>
                        </div>
                    </div>
                )}

                {/* Top Competitors Chart */}
                {topCompetitors.length > 0 && (
                    <div className="mb-6">
                        <div style={{ height: '400px' }}>
                            <Bar data={chartData} options={chartOptions} />
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 mb-6">
                    <button
                        onClick={fetchReport}
                        disabled={loading}
                        className={`px-6 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                            loading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                    >
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>

                    {results.length > 0 && (
                        <button
                            onClick={exportToCsv}
                            className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            Export CSV
                        </button>
                    )}
                </div>
            </div>

            {/* Results Table */}
            {loading ? (
                <div className="bg-white p-6 rounded-lg shadow text-center py-12">
                    <div className="text-gray-600">Loading report data...</div>
                </div>
            ) : results.length === 0 ? (
                <div className="bg-white p-6 rounded-lg shadow text-center py-12 text-gray-600">
                    No listings found with at least 1 unit in stock.
                </div>
            ) : (
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            Results ({results.length} listings)
                        </h3>
                    </div>

                    <div>
                        <table className="w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('asin')}
                                    >
                                        ASIN<SortIcon column="asin" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('productName')}
                                    >
                                        Product<SortIcon column="productName" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('quantity')}
                                    >
                                        Qty<SortIcon column="quantity" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('salesRank')}
                                    >
                                        Rank<SortIcon column="salesRank" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('mapPrice')}
                                    >
                                        MAP<SortIcon column="mapPrice" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('landedPrice')}
                                    >
                                        Landed<SortIcon column="landedPrice" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('buyboxWinner')}
                                    >
                                        Buybox Winner<SortIcon column="buyboxWinner" />
                                    </th>
                                    <th
                                        className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                    >
                                        Reason
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedResults.map((item, index) => (
                                    <tr key={`${item.asin}-${item.sku}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-2 py-2 whitespace-nowrap">
                                            <a
                                                href={`https://amazon.com/dp/${item.asin}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                            >
                                                {item.asin}
                                            </a>
                                        </td>
                                        <td className="px-2 py-2 text-gray-900 max-w-[250px] truncate" title={item.productName || ''}>
                                            {item.productName || <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-900">
                                            {item.quantity}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-900">
                                            {formatSalesRank(item.salesRank)}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-900">
                                            {formatCurrency(item.mapPrice)}
                                        </td>
                                        <td className="px-2 py-2 text-right text-gray-900">
                                            {formatCurrency(item.landedPrice)}
                                        </td>
                                        <td className="px-2 py-2 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                item.buyboxWinner === "Occam's Razor"
                                                    ? 'bg-green-100 text-green-800'
                                                    : item.buyboxWinner === 'Unknown'
                                                        ? 'bg-gray-100 text-gray-800'
                                                        : 'bg-red-100 text-red-800'
                                            }`}>
                                                {item.buyboxWinner}
                                            </span>
                                        </td>
                                        <td className="px-2 py-2 whitespace-nowrap">
                                            {item.landedPrice && item.mapPrice && item.landedPrice < item.mapPrice ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                    MAPP Violation
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImpactReport;
